import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Share,
  Visibility,
  PlayCircleOutline,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';

const NewsCard = ({ 
  news, 
  onShare, 
  onView,
  compact = false,
  gridView = false,
  onClick
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const extractYouTubeId = (text) => {
    if (!text) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
      /(?:youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/
    ];
    for (const pattern of patterns) {
      const match = text.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const youtubeId = extractYouTubeId(news.content);
  const isVideo = youtubeId || news.media_type === 'video' || news.video_url;

  useEffect(() => {
    setLoading(true);
    
    if (youtubeId) {
      setImageUrl(`https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`);
    } else if (news.featured_image || news.video_url) {
      const mediaUrl = news.video_url || news.featured_image;
      if (mediaUrl.startsWith('http')) {
        setImageUrl(mediaUrl);
      } else {
        const bucketName = 'devbucket96ac4-prod';
        const region = 'ap-south-1';
        setImageUrl(`https://${bucketName}.s3.${region}.amazonaws.com/public/${mediaUrl}`);
      }
    }
    setLoading(false);
  }, [news.featured_image, news.video_url, news.content, youtubeId]);

  const handleImageError = (e) => {
    if (youtubeId && e.target.src.includes('maxresdefault')) {
      e.target.src = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else {
      onView?.(news);
    }
  };

  // Grid view - compact card
  if (gridView) {
    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
        }}
        onClick={handleCardClick}
      >
        {loading ? (
          <Box sx={{ position: 'relative', paddingTop: '66.67%', bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200' }} />
        ) : imageUrl ? (
          <Box sx={{ position: 'relative', paddingTop: '66.67%', overflow: 'hidden', bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200' }}>
            {isVideo ? (
              <>
                <Box
                  component="img"
                  src={imageUrl}
                  alt={news.title}
                  loading="lazy"
                  decoding="async"
                  onError={handleImageError}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    bgcolor: 'rgba(0, 0, 0, 0.7)',
                    borderRadius: '50%',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayCircleOutline sx={{ fontSize: 20, color: 'white' }} />
                </Box>
              </>
            ) : (
              <Box
                component="img"
                src={imageUrl}
                alt={news.title}
                loading="lazy"
                onError={handleImageError}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            )}
            {news.is_breaking && (
              <Chip
                label="BREAKING"
                color="error"
                size="small"
                sx={{ position: 'absolute', top: 8, left: 8 }}
              />
            )}
          </Box>
        ) : null}
        <CardContent sx={{ flexGrow: 1, p: 1.5, pt: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
            {news.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {news.published_at ? format(new Date(news.published_at), 'MMM d, yyyy • h:mm a') : 'Recently'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Regular view
  return (
    <Card 
      sx={{ 
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
        ...(compact && { display: 'flex', flexDirection: 'column', height: '100%' })
      }}
      onClick={handleCardClick}
    >
      {loading ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: isMobile ? '75%' : '56.25%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
          }}
        />
      ) : imageUrl ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            paddingTop: isVideo ? '56.25%' : (isMobile ? '75%' : '56.25%'),
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.200',
          }}
        >
          {isVideo ? (
            <>
              <Box
                component="img"
                src={imageUrl}
                alt={news.title}
                loading="lazy"
                onError={handleImageError}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  right: 12,
                  bgcolor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayCircleOutline sx={{ fontSize: 24, color: 'white' }} />
              </Box>
            </>
          ) : (
            <Box
              component="img"
              src={imageUrl}
              alt={news.title}
              loading="lazy"
              decoding="async"
              onError={handleImageError}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
          {news.is_breaking && (
            <Chip
              label="BREAKING"
              color="error"
              size="small"
              sx={{ position: 'absolute', top: 8, left: 8 }}
            />
          )}
        </Box>
      ) : null}
      
      <CardContent sx={{ pt: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1, pr: 1 }}>
            {news.title}
          </Typography>
          <IconButton 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onShare?.(news);
            }}
          >
            <Share />
          </IconButton>
        </Box>

        {news.description && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {news.description}
          </Typography>
        )}

        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {news.is_breaking && (
            <Chip label="Breaking" color="error" size="small" />
          )}
          {news.is_featured && (
            <Chip label="Featured" color="primary" size="small" />
          )}
          <Chip label={news.category} size="small" variant="outlined" />
        </Box>

        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="caption" color="text.secondary">
              {news.source || 'HypeCircle'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              •
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {news.published_at ? format(new Date(news.published_at), 'MMM d, yyyy • h:mm a') : 'Recently'}
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {news.view_count || 0}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NewsCard;